import { environment } from '@utility/app.util';

import moment from 'moment';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';


import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { NotificationService } from '@utility/services/notification.service';
import { FUSE_LOADING_BAR_SKIP_HEADER } from '@fuse/services/loading/loading.interceptor';

import { DistributionChannelsResponseDto } from '@sociuu/interfaces';
import { IUserSyncStatus } from '@admin/sync-users/user-sync.interface';
import { ICountsSettings, ICountsSettingsServer } from '@lib/count.interface';

import { LAST_SYNC_FORMAT_12HR, LAST_SYNC_FORMAT_24HR, is12HoursFormat } from '@utility/date-time';

const REFRESH_STATUS_POLLING_TIMEOUT: number = 90_000;
const REFRESH_STATUS_POLLING_INTERVAL: number = 30_000;

export interface ICheckIpAccessServer {
  message: string;
  allowed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AppService {
  public environment: any = environment;

  private _countSettings: BehaviorSubject<ICountsSettings> = new BehaviorSubject(null);
  private _userCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public syncInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public lastSynchronized$: BehaviorSubject<string> = new BehaviorSubject<string>('You haven\'t sync yet');

  public get countSettings(): BehaviorSubject<ICountsSettings> {
    return this._countSettings;
  }

  public get usersCount(): number {
    return this.countSettings?.value?.navigation?.userCount;
  }

  public get currencySymbol(): string {
    return this.countSettings.value?.settings?.currencySymbol;
  }

  public get currencySymbolSign(): string {
    const currencyCode = this.currencySymbol;
    return currencyCode ? this._currencySymbolMap[currencyCode] || currencyCode : '';
  }

  private _currencySymbolMap: { [key: string]: string } = {
    'ARS': '$',
    'AUD': 'A$',
    'CAD': 'C$',
    'CHF': 'Fr',
    'DKK': 'kr',
    'EUR': '€',
    'GBP': '£',
    'INR': '₹',
    'NOK': 'kr',
    'NZD': 'NZ$',
    'PLN': 'zł',
    'RUB': '₽',
    'SEK': 'kr',
    'USD': '$',
    'ZAR': 'R'
  };

  constructor(private http: HttpClient, private notificationService: NotificationService) { }

  public checkIpAccess(): Observable<ICheckIpAccessServer> {
    const defaultMessage: ICheckIpAccessServer = {
      message: 'There is an error while checking IP access',
      allowed: false,
    };

    return this.http.get<{ data: ICheckIpAccessServer }>(`${environment.sociuuhubApiUrl}/signup/check-ip-access`)
      .pipe(
        map((response: { data: ICheckIpAccessServer }): ICheckIpAccessServer => response.data),
        catchError((err) => {
          defaultMessage.message = err || defaultMessage.message;
          console.error('Error checking IP access:', err);
          defaultMessage.message = err;
          return of(defaultMessage);
        }),
      );
  }

  public getUserCount(): Observable<number> {
    return this._userCountSubject.asObservable();
  }

  public getSyncInProgress(): Observable<boolean> {
    return this.syncInProgress$.asObservable();
  }

  public getLastSynchronized(): Observable<string> {
    return this.lastSynchronized$.asObservable();
  }

  public updateUserCount(silent: boolean = false): void {
    const options = silent
      ? { headers: new HttpHeaders({ [FUSE_LOADING_BAR_SKIP_HEADER]: 'true' }) }
      : {};

    this.http.get<{ data?: ICountsSettingsServer, success: boolean }>(`${environment.apiUrl}/counts-settings`, options).subscribe({
      next: (response): void => {
        const userCount = response?.data?.navigation?.userCount;
        if (typeof userCount === 'number') {
          this._userCountSubject.next(userCount);
        }
      },
      error: (err) => {
        console.error('Error updating user count:', err);
      }
    })
  }

  public getDistributions(): Observable<DistributionChannelsResponseDto> {
    return this.http.get<DistributionChannelsResponseDto>(`${environment.apiUrl}/distributions`);
  }

  public getCountSettings(): Observable<ICountsSettings> {
    return this.http.get(`${environment.apiUrl}/counts-settings`)
      .pipe(
        map((response: { data: ICountsSettingsServer, success: boolean }): ICountsSettings => {
          return this.transformCountsSettings(response.data);
        }),
        tap((response: ICountsSettings): void => {
          this.countSettings.next(response);
          this._userCountSubject.next(response.navigation.userCount);
        })
      );
  }

  public subscribeToSyncUsersStream(): Subscription {
    let isCompleted: boolean = false;
    return interval(REFRESH_STATUS_POLLING_INTERVAL)
      .pipe(
        // Start with 0 to make the API call immediately on component initialization
        startWith(0),
        // Switch to new observable for API call on every tick
        switchMap(() => this.getUserSyncStatus()),
        // Only continue if the 'status' property is true
      )
      .subscribe({
        next: (res) => {
          if (res.data.status === 1) {
            this.syncInProgress$.next(true);
          } else if (res.data.status === 0) {
            if (this.syncInProgress$.value) {
              isCompleted = true;
              this.notificationService.showSuccess('Users synchronization is completed.');
              this.lastSynchronized$.next((moment().format(is12HoursFormat() ? LAST_SYNC_FORMAT_12HR : LAST_SYNC_FORMAT_24HR)).toString());
              this.updateUserCount();
            }
            this.syncInProgress$.next(false);
          }
        },
        error: (err) => {
          console.error(err);
          isCompleted = true;
          this.syncInProgress$.next(false);
          this.notificationService.showError('Users synchronisation couldn\'t be completed.');
        },
        complete: () => {
          // if (!isCompleted) {
          //   this.syncInProgress$.next(false);
          //   this.notificationService.showWarning('Users synchronization couldn\'t be completed within the expected time. The process will continue in the background.');
          // } else {
          // isCompleted = false;
          // }
        }
      });
  }

  private getUserSyncStatus(): Observable<IUserSyncStatus> {
    return this.http.get<IUserSyncStatus>(`${environment.apiUrl}/v2/sync-progress/actimo`);
  }

  private transformCountsSettings(data: ICountsSettingsServer): ICountsSettings {
    return {
      navigation: {
        draftPostCount: data.navigation.draftPostCount,
        postsCount: data.navigation.postsCount,
        scheduledPostsCount: data.navigation.scheduled_posts_count,
        unreadNotificationCount: data.navigation.unreadNotificationCount,
        userCount: data.navigation.userCount,
        userGroupCount: data.navigation.userGroupCount,
      },
      settings: {
        companyLogo: data.settings.company_logo,
        currencySymbol: data.settings.currency_symbol,
      }
    }
  }
}
